import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import {LIFE_CYCLE_STATES, type RevaLifeCycleState} from "@reva/shared/lifecycle-state";

import {
    REVA_FILTER_TABS,
    type RevaFilterTab,
    type RevaSortOrder,
    type SearchValuationItem,
    type SearchValuationResult,
} from "../api/payload-types/search-valuation";
import type {TabCountsResult} from "../api/payload-types/tab-counts";
import {buildApiQueryError, type QueryError} from "../util/errorUtil";
import type {ApiQueryError} from "./types";

export interface ValuationsTableParams {
    pageSize: number;
    pageIndex: number;
    sortField: string;
    sortOrder: RevaSortOrder;
    /** Lifecycle states to include (OR-combined); null/empty = no filter. */
    filterLifeCycleState: RevaLifeCycleState[] | null;
    /**
     * Fixed per-tab filter (#19113), not user-changeable: always identical to the tab whose state
     * this params object lives in (the reducer enforces it), and sent as the request's filterTab.
     */
    filterTab: RevaFilterTab | null;
}

/**
 * Suchzustand EINES Startseiten-Tabs. Jeder Tab (WIP/POOL/CORRECTION/TEAMADMIN/REVISION) hat eine
 * eigene Kopie — ein Tab-Wechsel überschreibt daher nie Params/Daten eines anderen Tabs: Filter,
 * Sortierung, Seite und geladene Zeilen überleben den Hin- und Rückweg, und keine Instanz sieht
 * fremde Tab-Daten.
 */
interface ValuationsTabState {
    initialized: boolean;
    loading: boolean;
    needsRefresh: boolean;
    tableParams: ValuationsTableParams;
    count: number;
    valuations: SearchValuationItem[];
    /**
     * Lifecycle states this tab can ever contain for the current user (backend-computed from tab
     * semantics + permissions) — only these status-filter checkboxes are offered. null until the
     * first search response arrives.
     */
    possibleLifeCycleStates: RevaLifeCycleState[] | null;
    queryError: ApiQueryError | null;
}

/**
 * Freitext-getriebene Trefferzahlen je Tab für die Tab-Badges (gespeist aus POST /valuations/tab-counts).
 * Enthalten sind nur Tabs/Scopes, die der User sehen darf; ein fehlender Key bedeutet „keine Badge".
 */
interface TabCountsState {
    tabCounts: Partial<Record<RevaFilterTab, number>>;
    snapshotCounts: {own?: number; all?: number};
    loading: boolean;
}

/**
 * Gesamter Such-State der Startseite: ein unabhängiger Such-State je Tab, plus der GLOBALE
 * Live-Freitextfilter (er lebt in der einen Suchzeile und wird deshalb bewusst nicht je Tab
 * dupliziert) und die Tab-Badge-Zahlen.
 */
interface SearchValuationsState {
    tabs: Record<RevaFilterTab, ValuationsTabState>;
    freeText: string | null;
    tabCounts: TabCountsState;
}

/**
 * Normalize format of table params.
 * @param tab the tab whose state the params belong to — always wins over params.filterTab
 * @param params partial params to normalize
 */
const normalizeTableParams = (tab: RevaFilterTab, params: Partial<ValuationsTableParams>): ValuationsTableParams => {
    return {
        pageSize: 20,
        pageIndex: params.pageIndex ?? 0,
        sortField: params.sortField || "lastChangedDate",
        sortOrder: params.sortOrder || "DESC",
        filterLifeCycleState: params.filterLifeCycleState ?? null,
        filterTab: tab,
    };
};

/**
 * Check if two sets of table params are equal.
 * @return true if the parameter sets are equal
 */
export const checkTableParamsEquals = (paramsA: ValuationsTableParams, paramsB: ValuationsTableParams): boolean => {
    try {
        return JSON.stringify(paramsA) === JSON.stringify(paramsB);
    } catch (e) {
        console.error("Error comparing table params", e);
        return false;
    }
};

const initialTabState = (tab: RevaFilterTab): ValuationsTabState => ({
    initialized: false,
    loading: false,
    needsRefresh: true,

    tableParams: normalizeTableParams(tab, {}),

    count: 0,

    valuations: [],
    possibleLifeCycleStates: null,
    queryError: null,
});

const initialState: SearchValuationsState = {
    tabs: Object.fromEntries(REVA_FILTER_TABS.map((tab) => [tab, initialTabState(tab)])) as Record<
        RevaFilterTab,
        ValuationsTabState
    >,
    freeText: null,
    tabCounts: {tabCounts: {}, snapshotCounts: {}, loading: false},
};

export const searchValuationsSlice = createSlice({
    name: "searchValuations",
    initialState,
    reducers: {
        // Kein Reducer mutiert je in initialState hinein (Immer arbeitet auf Drafts), daher genügt
        // das geteilte initialState-Objekt als Reset-Wert. Setzt bewusst ALLES zurück (Logout/
        // Lizenzwechsel/resetAll) — inkl. Freitext und Tab-Counts.
        resetValuations: () => initialState,
        updateTableParams: (
            state,
            action: PayloadAction<{tab: RevaFilterTab; params: Partial<ValuationsTableParams>}>,
        ) => {
            const {tab, params} = action.payload;
            const tabState = state.tabs[tab];
            const updatedTableParams = normalizeTableParams(tab, params);
            if (!checkTableParamsEquals(tabState.tableParams, updatedTableParams)) {
                tabState.needsRefresh = true;
            }
            tabState.tableParams = updatedTableParams;
        },
        setLoading: (state, action: PayloadAction<RevaFilterTab>) => {
            const tabState = state.tabs[action.payload];
            tabState.initialized = true;
            tabState.loading = true;
            tabState.needsRefresh = false;
            tabState.queryError = null;
        },
        setPageLoaded: (state, action: PayloadAction<{tab: RevaFilterTab; result: SearchValuationResult}>) => {
            const tabState = state.tabs[action.payload.tab];
            const {count, valuations, possibleLifeCycleStates} = action.payload.result;
            tabState.loading = false;
            tabState.count = count;
            tabState.valuations = valuations;
            // Rollout-Fallback: ein älteres Backend liefert das Feld noch nicht — dann alle Stati anbieten.
            tabState.possibleLifeCycleStates = possibleLifeCycleStates ?? [...LIFE_CYCLE_STATES];
        },
        setQueryError: (state, action: PayloadAction<{tab: RevaFilterTab; error: QueryError}>) => {
            const tabState = state.tabs[action.payload.tab];
            tabState.loading = false;
            tabState.count = 0;
            tabState.valuations = [];
            tabState.queryError = buildApiQueryError(action.payload.error);
        },
        /**
         * Setzt den GLOBALEN Live-Freitextfilter (null = kein Filter). Markiert jeden Tab als
         * needsRefresh, sodass er bei der nächsten Aktivierung mit dem neuen Filter neu lädt; der
         * gerade gemountete Tab lädt sich sofort neu (siehe Freitext-Effekt in ValuationsTable).
         */
        setFreeText: (state, action: PayloadAction<string | null>) => {
            state.freeText = action.payload;
            for (const tab of REVA_FILTER_TABS) {
                state.tabs[tab].needsRefresh = true;
                // Ein geänderter Filter ändert die Treffermenge — Seiten-Offsets der alten Menge
                // würden eine leere Seite zeigen, obwohl die Badge Treffer meldet.
                state.tabs[tab].tableParams.pageIndex = 0;
            }
        },
        setTabCountsLoading: (state) => {
            state.tabCounts.loading = true;
        },
        setTabCounts: (state, action: PayloadAction<TabCountsResult>) => {
            state.tabCounts.loading = false;
            state.tabCounts.tabCounts = action.payload.tabCounts ?? {};
            state.tabCounts.snapshotCounts = action.payload.snapshotCounts ?? {};
        },
        // Im Fehlerfall die Zahlen leeren (statt veraltete Werte zu behalten) und den Loading-State
        // beenden — ein fehlgeschlagener Count-Lauf zeigt schlicht keine Badges.
        setTabCountsError: (state) => {
            state.tabCounts.loading = false;
            state.tabCounts.tabCounts = {};
            state.tabCounts.snapshotCounts = {};
        },
    },
    selectors: {
        selectInitialized: (state, tab: RevaFilterTab) => state.tabs[tab].initialized,
        selectLoading: (state, tab: RevaFilterTab) => state.tabs[tab].loading,
        selectNeedsRefresh: (state, tab: RevaFilterTab) => state.tabs[tab].needsRefresh,
        selectTableParams: (state, tab: RevaFilterTab) => state.tabs[tab].tableParams,
        selectCount: (state, tab: RevaFilterTab) => state.tabs[tab].count,
        selectValuations: (state, tab: RevaFilterTab) => state.tabs[tab].valuations,
        selectPossibleLifeCycleStates: (state, tab: RevaFilterTab) => state.tabs[tab].possibleLifeCycleStates,
        selectQueryError: (state, tab: RevaFilterTab) => state.tabs[tab].queryError,
        selectFreeText: (state) => state.freeText,
        selectTabCounts: (state) => state.tabCounts,
    },
});

export const {
    resetValuations,
    updateTableParams,
    setLoading,
    setPageLoaded,
    setQueryError,
    setFreeText,
    setTabCountsLoading,
    setTabCounts,
    setTabCountsError,
} = searchValuationsSlice.actions;

export const {
    selectInitialized,
    selectLoading,
    selectNeedsRefresh,
    selectTableParams,
    selectCount,
    selectValuations,
    selectPossibleLifeCycleStates,
    selectQueryError,
    selectFreeText,
    selectTabCounts,
} = searchValuationsSlice.selectors;

export default searchValuationsSlice.reducer;
